import { Stack, Typography } from '@mui/material';

import { ExportConnectionEnum } from '@verantos-dev/data-access-export-connections';
import { Icon, TruncatedTooltip } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { ExportConnectionDetailsType } from '@verantos-dev/types-cohorts';

import { getExportLocationDisplayText } from '../../hooks/useExportCohortModal';
import { useExportCohortModalStrings } from '../../hooks/useExportCohortModalStrings';

type ExportCohortModalSubmittedProps = {
  projectName?: string;
  cohortName?: string;
  schemaName?: string;
  selectedExportLocation?: ExportConnectionDetailsType;
};

const getS3DestinationPath = (connection: ExportConnectionDetailsType): string => {
  if (connection.connectionType !== ExportConnectionEnum.S3) return '';
  const { bucket, endpoint } = connection.connectionDetails;
  const trimmedEndpoint = endpoint?.replace(/^\/+|\/+$/g, '');
  if (trimmedEndpoint) {
    return `s3://${bucket}/${trimmedEndpoint}/`;
  }
  return `s3://${bucket}/`;
};

export const ExportCohortModalSubmitted: React.FC<ExportCohortModalSubmittedProps> = ({
  projectName,
  cohortName,
  schemaName,
  selectedExportLocation,
}) => {
  const { contentMessages } = useExportCohortModalStrings();

  return (
    <Stack marginTop={4} display={'flex'} gap={4} direction={'column'}>
      <Stack direction={'column'} gap={4} width={'100%'}>
        <Stack direction={'column'} gap={3} minWidth={0}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            fontSize={'12px'}
            color={theme.palette.text.primary}
            style={{ textTransform: 'uppercase' }}
          >
            {contentMessages.project}
          </Typography>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-project`} icon={'project'} width={20} height={20} />
            <Stack minWidth={0}>
              <TruncatedTooltip
                id="export-cohort-submitted-project-name-tooltip"
                title={projectName}
              >
                <Typography
                  fontSize={'14px'}
                  color={theme.palette.text.primary}
                  overflow={'hidden'}
                  textOverflow={'ellipsis'}
                  whiteSpace={'nowrap'}
                >
                  {projectName}
                </Typography>
              </TruncatedTooltip>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.projectName}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction={'column'} gap={3} minWidth={0}>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            fontSize={'12px'}
            color={theme.palette.text.primary}
            style={{ textTransform: 'uppercase' }}
          >
            {contentMessages.cohort}
          </Typography>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-cohort`} icon={'cohort'} width={20} height={20} />
            <Stack minWidth={0}>
              <TruncatedTooltip id="export-cohort-submitted-cohort-name-tooltip" title={cohortName}>
                <Typography
                  fontSize={'14px'}
                  color={theme.palette.text.primary}
                  overflow={'hidden'}
                  textOverflow={'ellipsis'}
                  whiteSpace={'nowrap'}
                >
                  {cohortName}
                </Typography>
              </TruncatedTooltip>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.cohortName}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction={'column'} gap={3}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          fontSize={'12px'}
          color={theme.palette.text.primary}
          style={{ textTransform: 'uppercase' }}
        >
          {contentMessages.location}
        </Typography>
        <Stack direction={'column'} gap={3}>
          <Stack direction={'row'} gap={2}>
            <Icon id={`exportSuccess-connector`} icon={'connector'} width={20} height={20} />
            <Stack direction={'column'} minWidth={0}>
              <TruncatedTooltip
                id="export-cohort-submitted-location-tooltip"
                title={
                  selectedExportLocation ? getExportLocationDisplayText(selectedExportLocation) : ''
                }
              >
                <Typography
                  fontSize={'14px'}
                  color={theme.palette.text.primary}
                  overflow={'hidden'}
                  textOverflow={'ellipsis'}
                  whiteSpace={'nowrap'}
                >
                  {selectedExportLocation
                    ? getExportLocationDisplayText(selectedExportLocation)
                    : ''}
                </Typography>
              </TruncatedTooltip>
              <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                {contentMessages.exportLocation}
              </Typography>
            </Stack>
          </Stack>
          {selectedExportLocation?.connectionType === ExportConnectionEnum.SNOWFLAKE && (
            <Stack direction={'row'} gap={2} maxWidth={'100%'}>
              <Icon id={`exportSuccess-schema`} icon={'schema'} width={20} height={20} />
              <Stack direction={'column'} minWidth={0} flex={1}>
                <TruncatedTooltip id="export-cohort-submitted-schema-tooltip" title={schemaName}>
                  <Typography
                    fontSize={'14px'}
                    color={theme.palette.text.primary}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                  >
                    {schemaName}
                  </Typography>
                </TruncatedTooltip>
                <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                  {contentMessages.schemaName}
                </Typography>
              </Stack>
            </Stack>
          )}
          {selectedExportLocation?.connectionType === ExportConnectionEnum.S3 && (
            <Stack direction={'row'} gap={2} maxWidth={'100%'}>
              <Icon id={`exportSuccess-filePath`} icon={'filePath'} width={20} height={20} />
              <Stack direction={'column'} minWidth={0} flex={1}>
                <TruncatedTooltip
                  id="export-cohort-submitted-path-tooltip"
                  title={getS3DestinationPath(selectedExportLocation)}
                >
                  <Typography
                    fontSize={'14px'}
                    color={theme.palette.text.primary}
                    overflow={'hidden'}
                    textOverflow={'ellipsis'}
                    whiteSpace={'nowrap'}
                  >
                    {getS3DestinationPath(selectedExportLocation)}
                  </Typography>
                </TruncatedTooltip>
                <Typography fontSize={'12px'} color={theme.palette.text.secondary}>
                  {contentMessages.path}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
